import React, { useEffect, useState, useMemo } from "react";
import { COLOR_CODES } from "../../common";
import { ElectricityParData } from "../../models/parData";
import {
  ChartData,
  ChartDataParams,
  FormatChartData,
} from "../../components/recharts/functions";
import { ReMixChart } from "../../components/recharts/ReMixCharts";
import { ChartType } from "../../models/chart_model";
import { formatDateToDDMMYYYY, getBuyerSellersData, getElectricityParData } from "../../Rest_api/restapi";
import { FilterTab } from "../../components/Filters";
import { ChartTable } from "../../components/table/SummaryTable";
import BuyerSellerChart from "./TopBuyersSellersPopup";

// Constants for chart configuration
const views = ["Chart", "Table"];
const exchanges = ["IEX"];
const products = ["DAM"];
const regions = ["WRLDC", "ERLDC", "NRLDC", "SRLDC", "NERLDC"];
const type = ["Buyers & Sellers", "Regions", "Trends"];

const CHART_LEGENDS = [
  {
    name: "MCV",
    dataKey: "_mcv_million_mwh",
    stroke: COLOR_CODES.secondary,
    unit: "Million MWh",
    type: ChartType.Bar,
  },
  {
    name: "Wt. Avg Price",
    dataKey: "_wt_mcp_rs_mwh",
    stroke: COLOR_CODES.primary,
    yAxisId: "right",
    unit: "Rs/MWh",
    type: ChartType.Line,
  },
  {
    name: "Sell Bid",
    dataKey: "_sell_bid_million_mwh",
    color: "green",
    type: ChartType.Line,
    unit: "Million MWh",
  },
  {
    name: "Purchase Bid",
    dataKey: "_purchase_bid_million_mwh",
    color: "yellow",
    type: ChartType.Line,
    unit: "Million MWh",
  },
  {
    name: "Generation",
    dataKey: "_generation_million_mwh",
    color: "blue",
    type: ChartType.Line,
    unit: "Million MWh",
  },

  {
    name: "Consumption",
    dataKey: "_consumption_million_mwh",
    color: "red",
    type: ChartType.Line,
    unit: "Million MWh",
  },
  {
    name: "Outage",
    dataKey: "_outage_million_mwh",
    color: "purple",
    type: ChartType.Line,
    unit: "Million MWh",
  },
];

function ElectricityPar({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) {
  const [parData, setParData] = useState<ElectricityParData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    region_buyer: any[];
    region_seller: any[];
    buyer: any[];
    seller: any[];
  }>({ region_buyer: [], region_seller: [], buyer: [], seller: [] });
  const [isBuyerSellerLoading, setIsBuyerSellerLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getElectricityParData(startDate, endDate);
        setParData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        console.error("Error fetching electricity PAR data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
   const fetchBuyerSeller = async () => {
    try {
      setIsBuyerSellerLoading(true);
      const startDateStr = formatDateToDDMMYYYY(startDate);
      const endDateStr = formatDateToDDMMYYYY(endDate);
      const formData = new FormData();
      formData.append("start_date", startDateStr);
      formData.append("end_date", endDateStr);
      formData.append("exchange", exchanges.join(","));
      formData.append("product", products.join(","));
      formData.append("region", regions.join(","));
      const data = await getBuyerSellersData(formData);
      setData(data);
    } catch (err) {
      console.error("Error fetching buyer/seller data:", err);
    } finally {
      setIsBuyerSellerLoading(false);
    }
  }
  fetchBuyerSeller();
  },[startDate,endDate]);
  return (
    <div className="h-full w-full flex flex-col bg-gray-50 p-3 overflow-scroll lg:min-h-0 lg:overflow-hidden min-h-[1900px]">
      {/* Layout with Combined Top Cards */}
      <div className="flex flex-col flex-1 gap-3 min-h-0">
        {/* Combined Top Cards (Card 1 + Card 2) */}
        <div className="bg-white rounded-xl shadow-md border p-3 border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0">
          <div className="text-left h-full w-full flex flex-col">
            <ElectricityParCard 
              parData={parData} 
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>

        {/* Bottom Row - 2 Cards Side by Side */}
        <div className="flex flex-col lg:flex-row flex-1 gap-3 min-h-0">
          {/* Card 3 - Top Buyers */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0 lg:min-h-0">
            <div className="text-left h-full w-full flex flex-col">
              <div className="flex-1 min-h-0">
                <BuyerCard data={data} isLoading={isBuyerSellerLoading} />
              </div>
            </div>
          </div>

          {/* Card 4 - Top Sellers */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex-1 min-h-0">
            <div className="text-left h-full w-full flex flex-col">
              <div className="flex-1 min-h-0">
                <SellerCard data={data} isLoading={isBuyerSellerLoading} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 mt-2">
        <div
          className="text-left text-xs"
          style={{ color: COLOR_CODES.secondary }}
        >
          Data Source:{" "}
          <a
            href="https://www.iexindia.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            IEX
          </a>
          ,{" "}
          <a
            href="http://npp.gov.in"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            NPP
          </a>
          ,{" "}
          <a
            href="https://cea.nic.in"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            CEA
          </a>
          ,{" "}
          <a
            href="https://grid-india.in/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            Grid India
          </a>
        </div>
      </div>
    </div>
  );
      function ElectricityParCard({ 
      parData, 
      isLoading, 
      error 
    }: { 
      parData: ElectricityParData[]; 
      isLoading: boolean; 
      error: string | null; 
    }) {
    const [selectedView, setSelectedView] = useState(views[0]);

    // Use useMemo to avoid recalculating chart data on every render
    const chartData = useMemo(() => {
      if (!parData.length) {
        return {
          keys: [],
          unit_keys: [],
          keysLabels: [],
          group_by: {},
          data: [],
        };
      }

      const chartParams: ChartDataParams = {
        data: parData,
        keys: ["date"],
        volumeKey: "mcv_million_mwh",
        aggregationKeys: [
          "purchase_bid_million_mwh",
          "sell_bid_million_mwh",
          "generation_million_mwh",
          "consumption_million_mwh",
          "outage_million_mwh",
        ],
        groupBy: [],
        priceKey: "wt_mcp_rs_mwh",
      };

      return FormatChartData(chartParams);
    }, [parData]);

        if (isLoading) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">
                        Loading electricity PAR data...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="text-center text-red-600">
                    <p className="text-sm">Error: {error}</p>
                </div>
            </div>
        );
    }

    if (!parData.length) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

        return (
        <>
         <div className="text-left h-full w-full flex flex-col">
            <div className="flex flex-row justify-between gap-x-2">
             <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading">
               IEX DAM, Consumption, Generation and Outage Analysis
             </h3>
             <div className='flex flex-row gap-x-2 pb-2'>
              <FilterTab
                options={views}
                selected={[selectedView]}
                onClick={(e) => setSelectedView(e)}
             /></div>
           </div>
           <div className="flex-1 min-h-0">
             <div className='h-full w-full flex flex-col'>
               <div className="flex-1 min-h-0">
                 {selectedView === "Chart" && (
                   <ReMixChart
                     data={chartData.data}
                     xDataKey="date"
                     yAxisLabel="Million MWh"
                     secondYAxisWidth={50}
                     yAxisWidth={45}
                     showSecondYAxis={true}
                     onlyTitle={true}
                     secondYAxisLabel="Rs/MWh"
                     unit=""
                     showBrush={true}
                     legends={CHART_LEGENDS}
                   />
                 )}
                 {selectedView === "Table" && (
                   <ChartTable
                     data={chartData.data}
                     legends={CHART_LEGENDS}
                     extraKeys={["date"]}
                     extraLabels={["Date"]}
                   />
                 )}
               </div>
             </div>
           </div>
         </div>
        </>
      );
  }
}

export default ElectricityPar;



function BuyerCard({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-xs text-gray-600">Loading buyer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLOR_CODES.primary }}></div>
            <span className="text-sm font-medium text-gray-700">Active Buyers</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-200 px-2 py-1 rounded-full">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            {data.buyer?.filter((b: { value: number }) => b.value > 0).length || 0}
          </div>
        </div>

        {/* Total Volume */}
        <div className="mt-1 flex flex-row justify-between">
          <div className="text-sm text-gray-500 font-medium">Total Volume</div>
          <div className="text-sm font-bold" style={{ color: COLOR_CODES.primary }}>
            {(data.buyer
              ?.reduce((sum: any, item: { value: any }) => sum + item.value, 0)/1000000)
              .toFixed(2)}{" "}
            Million MWh
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="flex-1 min-h-0">
        <div className="h-full w-full overflow-y-auto">
          <BuyerSellerChart
            data={data.buyer || []}
            showLegend={false}
            type="buyer"
            theme="light"
          />
        </div>
      </div>
    </div>
  );
}

function SellerCard({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-xs text-gray-600">Loading seller data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b rounded-t-lg" style={{ backgroundColor: COLOR_CODES.secondary }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white"></div>
            <span className="text-sm font-medium text-white">Active Sellers</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-300 bg-gray-700 px-2 py-1 rounded-full">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {data.seller?.filter((s: { value: number }) => s.value > 0).length || 0}
          </div>
        </div>

        {/* Total Volume */}
        <div className="mt-1 flex flex-row justify-between">
          <div className="text-sm text-gray-300 font-medium">Total Volume</div>
          <div className="text-sm font-bold text-white">
            {(data.seller
              ?.reduce((sum: any, item: { value: any }) => sum + item.value, 0)/1000000)
              .toFixed(2)}{" "}
            Million MWh
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="flex-1 min-h-0">
        <div className="h-full w-full overflow-y-auto">
          <BuyerSellerChart
            data={data.seller || []}
            showLegend={false}
            type="seller"
            theme="light"
          />
        </div>
      </div>
    </div>
  );
}
  